import React, { useState } from 'react';
import { Check, ChevronRight, ChevronLeft, Building, Users, Target, Zap } from 'lucide-react';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  component: React.ReactNode;
}

interface OnboardingData {
  companyInfo: {
    name: string;
    industry: string;
    size: string;
    location: string;
  };
  sustainabilityGoals: {
    primaryGoal: string;
    targetYear: string;
    reductionTarget: string;
    focusAreas: string[];
  };
  dataSetup: {
    facilities: string[];
    dataCollectionMethod: string;
    reportingStandard: string;
  };
}

const initialData: OnboardingData = {
  companyInfo: {
    name: '',
    industry: '',
    size: '',
    location: ''
  },
  sustainabilityGoals: {
    primaryGoal: '',
    targetYear: '',
    reductionTarget: '',
    focusAreas: []
  },
  dataSetup: {
    facilities: [],
    dataCollectionMethod: '',
    reportingStandard: ''
  }
};

interface OnboardingWizardProps {
  onComplete: (data: OnboardingData) => void;
  onSkip: () => void;
}

export default function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<OnboardingData>(initialData);

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Sustainalyze',
      description: 'Let\'s get you set up for sustainability success',
      icon: <Zap className="h-6 w-6" />,
      component: <WelcomeStep onSkip={onSkip} />
    },
    {
      id: 'company',
      title: 'Company Information',
      description: 'Tell us about your organization',
      icon: <Building className="h-6 w-6" />,
      component: (
        <CompanyInfoStep 
          data={data.companyInfo}
          onChange={(companyInfo) => setData(prev => ({ ...prev, companyInfo }))}
        />
      )
    },
    {
      id: 'goals',
      title: 'Sustainability Goals',
      description: 'Define your environmental objectives',
      icon: <Target className="h-6 w-6" />,
      component: (
        <GoalsStep 
          data={data.sustainabilityGoals}
          onChange={(sustainabilityGoals) => setData(prev => ({ ...prev, sustainabilityGoals }))}
        />
      )
    },
    {
      id: 'setup',
      title: 'Data Setup',
      description: 'Configure your data collection',
      icon: <Users className="h-6 w-6" />,
      component: (
        <DataSetupStep 
          data={data.dataSetup}
          onChange={(dataSetup) => setData(prev => ({ ...prev, dataSetup }))}
        />
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(data);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepComplete = (stepIndex: number) => {
    if (stepIndex === 0) return true; // Welcome step
    if (stepIndex === 1) return data.companyInfo.name && data.companyInfo.industry;
    if (stepIndex === 2) return data.sustainabilityGoals.primaryGoal && data.sustainabilityGoals.targetYear;
    if (stepIndex === 3) return data.dataSetup.reportingStandard && data.dataSetup.dataCollectionMethod;
    return false;
  };

  const canProceed = isStepComplete(currentStep);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Progress Bar */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Setup Guide</h2>
            <span className="text-sm text-gray-500">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  index < currentStep 
                    ? 'bg-green-600 border-green-600' 
                    : index === currentStep
                    ? 'border-blue-600 bg-blue-600'
                    : 'border-gray-300'
                }`}>
                  {index < currentStep ? (
                    <Check className="h-4 w-4 text-white" />
                  ) : (
                    <span className={`text-sm font-medium ${
                      index === currentStep ? 'text-white' : 'text-gray-500'
                    }`}>
                      {index + 1}
                    </span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-2 ${
                    index < currentStep ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="flex items-center mb-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 mr-4">
              {React.cloneElement(steps[currentStep].icon, { className: "h-6 w-6 text-blue-600" })}
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">{steps[currentStep].title}</h3>
              <p className="text-sm text-gray-600">{steps[currentStep].description}</p>
            </div>
          </div>
          
          {steps[currentStep].component}
        </div>

        {/* Navigation */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </button>

          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                onClick={onSkip}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
              >
                Skip Setup
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep === steps.length - 1 ? 'Complete Setup' : 'Next'}
              <ChevronRight className="h-4 w-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step Components
function WelcomeStep({ onSkip: _onSkip }: { onSkip: () => void }) {
  return (
    <div className="text-center space-y-6">
      <div className="mx-auto w-24 h-24 bg-gradient-to-br from-green-400 to-blue-600 rounded-full flex items-center justify-center">
        <Zap className="h-12 w-12 text-white" />
      </div>
      <div className="space-y-4">
        <h3 className="text-2xl font-bold text-gray-900">Welcome to Sustainalyze!</h3>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          We'll help you set up your sustainability tracking in just a few minutes. 
          This setup will customize the platform to your organization's needs.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <div className="text-center">
          <Building className="h-8 w-8 text-blue-600 mx-auto mb-2" />
          <h4 className="font-medium text-gray-900">Company Profile</h4>
          <p className="text-sm text-gray-600">Tell us about your organization</p>
        </div>
        <div className="text-center">
          <Target className="h-8 w-8 text-green-600 mx-auto mb-2" />
          <h4 className="font-medium text-gray-900">Set Goals</h4>
          <p className="text-sm text-gray-600">Define your sustainability targets</p>
        </div>
        <div className="text-center">
          <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
          <h4 className="font-medium text-gray-900">Configure Data</h4>
          <p className="text-sm text-gray-600">Set up data collection methods</p>
        </div>
      </div>
    </div>
  );
}

interface CompanyInfoStepProps {
  data: OnboardingData['companyInfo'];
  onChange: (data: OnboardingData['companyInfo']) => void;
}

function CompanyInfoStep({ data, onChange }: CompanyInfoStepProps) {
  const industries = [
    'Technology', 'Manufacturing', 'Healthcare', 'Finance', 'Retail', 'Energy', 'Transportation', 'Other'
  ];

  const companySizes = [
    'Startup (1-10 employees)',
    'Small (11-50 employees)', 
    'Medium (51-200 employees)',
    'Large (201-1000 employees)',
    'Enterprise (1000+ employees)'
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Name *
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange({ ...data, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your company name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industry *
          </label>
          <select
            value={data.industry}
            onChange={(e) => onChange({ ...data, industry: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select industry</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Size
          </label>
          <select
            value={data.size}
            onChange={(e) => onChange({ ...data, size: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select size</option>
            {companySizes.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Location
          </label>
          <input
            type="text"
            value={data.location}
            onChange={(e) => onChange({ ...data, location: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., San Francisco, CA"
          />
        </div>
      </div>
    </div>
  );
}

interface GoalsStepProps {
  data: OnboardingData['sustainabilityGoals'];
  onChange: (data: OnboardingData['sustainabilityGoals']) => void;
}

function GoalsStep({ data, onChange }: GoalsStepProps) {
  const primaryGoals = [
    'Carbon Neutrality',
    'Net Zero Emissions',
    'Energy Efficiency',
    'Waste Reduction',
    'Sustainable Supply Chain',
    'ESG Compliance'
  ];

  const focusAreasOptions = [
    'Scope 1 Emissions',
    'Scope 2 Emissions', 
    'Scope 3 Emissions',
    'Renewable Energy',
    'Waste Management',
    'Water Conservation',
    'Sustainable Transportation',
    'Green Building'
  ];

  const handleFocusAreaChange = (area: string, checked: boolean) => {
    const newFocusAreas = checked
      ? [...data.focusAreas, area]
      : data.focusAreas.filter(a => a !== area);
    onChange({ ...data, focusAreas: newFocusAreas });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Sustainability Goal *
          </label>
          <select
            value={data.primaryGoal}
            onChange={(e) => onChange({ ...data, primaryGoal: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select primary goal</option>
            {primaryGoals.map(goal => (
              <option key={goal} value={goal}>{goal}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Year *
          </label>
          <select
            value={data.targetYear}
            onChange={(e) => onChange({ ...data, targetYear: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select target year</option>
            {[2025, 2026, 2027, 2028, 2029, 2030, 2035, 2040, 2050].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Emission Reduction Target
        </label>
        <select
          value={data.reductionTarget}
          onChange={(e) => onChange({ ...data, reductionTarget: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select reduction target</option>
          <option value="25%">25% reduction</option>
          <option value="50%">50% reduction</option>
          <option value="75%">75% reduction</option>
          <option value="100%">100% reduction (Net Zero)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Focus Areas (select all that apply)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {focusAreasOptions.map(area => (
            <label key={area} className="flex items-center">
              <input
                type="checkbox"
                checked={data.focusAreas.includes(area)}
                onChange={(e) => handleFocusAreaChange(area, e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">{area}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

interface DataSetupStepProps {
  data: OnboardingData['dataSetup'];
  onChange: (data: OnboardingData['dataSetup']) => void;
}

function DataSetupStep({ data, onChange }: DataSetupStepProps) {
  const reportingStandards = [
    'GRI (Global Reporting Initiative)',
    'SASB (Sustainability Accounting Standards Board)',
    'CDP (Carbon Disclosure Project)',
    'TCFD (Task Force on Climate-related Financial Disclosures)',
    'Custom Framework'
  ];

  const collectionMethods = [
    'Manual Entry',
    'CSV/Excel Upload',
    'API Integration',
    'IoT Sensors',
    'Third-party Providers'
  ];

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reporting Standard *
        </label>
        <select
          value={data.reportingStandard}
          onChange={(e) => onChange({ ...data, reportingStandard: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select reporting standard</option>
          {reportingStandards.map(standard => (
            <option key={standard} value={standard}>{standard}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Primary Data Collection Method *
        </label>
        <select
          value={data.dataCollectionMethod}
          onChange={(e) => onChange({ ...data, dataCollectionMethod: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select collection method</option>
          {collectionMethods.map(method => (
            <option key={method} value={method}>{method}</option>
          ))}
        </select>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">What's Next?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Your dashboard will be customized based on these settings</li>
          <li>• You can start adding emission data immediately</li>
          <li>• We'll help you set up automated data collection</li>
          <li>• You can modify these settings anytime in preferences</li>
        </ul>
      </div>
    </div>
  );
}